import React, { useMemo } from 'react';
import { getFrequencyLabel, getDisplayFrequency } from '../../../../../utils/types';
import { getFrequencyDescription, getOperatingDates } from '../../../../../utils/frequencyMatcher';
import { useTimezone } from '../../../../../contexts/TimezoneContext';
import { computeLocalFrequency, computeUTCFrequency } from '../../../../../utils/timeUtils';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import LockIcon from '@mui/icons-material/Lock';

interface FrequencyDaySelectorProps {
  frequency: string;
  frequencyMYT?: string;
  onChange: (frequency: string) => void;
  error?: string | null;
  onBlur?: () => void;
  startDate?: string;
  endDate?: string;
  lockedDays?: string[];
  /** First station STD in UTC (e.g., "2200") — needed for MYT ↔ UTC conversion on toggle */
  stdUTC?: string;
  /** First station UTC offset (e.g., "+0800") — defaults to +0800 in MYT mode */
  utcOffset?: string;
}

// Quick select presets with Material-UI icons
const PRESETS = [
  { value: '1234567', label: 'Daily', Icon: CalendarTodayIcon, description: 'All 7 days' },
  { value: '12345', label: 'Weekdays', Icon: BusinessCenterIcon, description: 'Mon-Fri' },
  { value: '67', label: 'Weekends', Icon: BeachAccessIcon, description: 'Sat-Sun' },
];

export function FrequencyDaySelector({
  frequency,
  frequencyMYT,
  onChange,
  error,
  onBlur,
  startDate,
  endDate,
  lockedDays = [],
  stdUTC,
  utcOffset,
}: FrequencyDaySelectorProps) {
  const { timezone } = useTimezone();
  const hasError = error && error.trim() !== '';
  const freqRate = ''; // Always weekly - airport standard

  // Use getDisplayFrequency (same as all other components) for MYT display.
  // Falls back to live computation if frequencyMYT isn't available but stdUTC is.
  const isMYT = timezone === 'MYT';
  const effectiveOffset = utcOffset || '+0800'; // MYT is always UTC+8
  const canConvert = isMYT && !!stdUTC;

  const displayFrequency = isMYT
    ? (frequencyMYT || (stdUTC ? computeLocalFrequency(frequency, stdUTC, effectiveOffset) : frequency))
    : frequency;

  const days = [
    { day: '1', label: 'Mon', fullLabel: 'Monday' },
    { day: '2', label: 'Tue', fullLabel: 'Tuesday' },
    { day: '3', label: 'Wed', fullLabel: 'Wednesday' },
    { day: '4', label: 'Thu', fullLabel: 'Thursday' },
    { day: '5', label: 'Fri', fullLabel: 'Friday' },
    { day: '6', label: 'Sat', fullLabel: 'Saturday' },
    { day: '7', label: 'Sun', fullLabel: 'Sunday' },
  ];

  // Day-count hints for short date ranges (≤ 60 days)
  const dayCounts = useMemo(() => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays > 60) return null;
    const counts: Record<string, number> = {};
    for (const iataDay of ['1', '2', '3', '4', '5', '6', '7']) {
      const dates = getOperatingDates(iataDay, start, end, start, end);
      counts[iataDay] = dates.length;
    }
    return counts;
  }, [startDate, endDate]);

  // Generate visual preview: "_2_4____" format
  const getPreview = () => {
    const currentFreq = displayFrequency.split('').filter(d => d >= '1' && d <= '7');
    return days.map(({ day }) =>
      currentFreq.includes(day) ? day : '_'
    ).join('');
  };

  const handleToggle = (day: string) => {
    if (lockedDays.includes(day)) return;

    // Work with the display frequency (local in MYT mode, UTC otherwise)
    const currentFreq = displayFrequency.split('').filter(d => d >= '1' && d <= '7');
    let newFreq: string[];

    if (currentFreq.includes(day)) {
      newFreq = currentFreq.filter(d => d !== day);
    } else {
      newFreq = [...currentFreq, day].sort();
    }

    const newFreqString = newFreq.join('');
    // Convert back to UTC if in MYT mode and we can convert
    onChange(canConvert ? computeUTCFrequency(newFreqString, stdUTC!, effectiveOffset) : newFreqString);
  };

  const handlePresetClick = (presetValue: string) => {
    // Union preset value with locked days so locked days are never removed
    const merged = new Set([...presetValue.split(''), ...lockedDays]);
    const localResult = ['1','2','3','4','5','6','7'].filter(d => merged.has(d)).join('');
    // Convert back to UTC if in MYT mode and we can convert
    onChange(canConvert ? computeUTCFrequency(localResult, stdUTC!, effectiveOffset) : localResult);
  };

  return (
    <div className="block mb-2">
      <span className="block mb-1.5 text-base font-semibold text-gray-600">
        Operating Days
        {isMYT && canConvert && <span className="text-blue-500 text-xs font-normal ml-2">(MYT)</span>}
        {isMYT && !canConvert && <span className="text-gray-400 text-xs font-normal ml-2">(UTC — add station data for MYT)</span>}
        <span className="text-danger-500 ml-1">*</span>
      </span>

      {/* Quick Select Presets Row */}
      <div className="flex gap-2 items-center mb-2">
        <span className="text-sm text-gray-600 py-2 font-medium">Quick select:</span>
        {PRESETS.map((preset) => {
          const isActive = displayFrequency === preset.value;
          // Disable preset if any of its days don't exist in the date range
          const isPresetDisabled = dayCounts !== null &&
            preset.value.split('').some(d => dayCounts[d] === 0);
          const IconComponent = preset.Icon;
          return (
            <button
              key={preset.value}
              type="button"
              onClick={() => handlePresetClick(preset.value)}
              disabled={isPresetDisabled}
              title={isPresetDisabled
                ? `${preset.description} — no matching days in date range`
                : preset.description}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border-2 transition-all flex items-center gap-1.5
                ${isPresetDisabled
                  ? 'opacity-40 cursor-not-allowed bg-gray-100 border-gray-200 text-gray-400'
                  : isActive
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400 hover:bg-primary-50'
                }`}
            >
              <IconComponent sx={{ fontSize: 16 }} />
              {preset.label}
            </button>
          );
        })}
      </div>

      {/* Day Selector - Using grid for perfect alignment */}
      <div className="grid grid-cols-8 gap-2 mb-3">
        {days.map(({ day, label, fullLabel }) => {
          const isSelected = displayFrequency.includes(day);
          const isLocked = lockedDays.includes(day);
          const isDisabled = dayCounts !== null && dayCounts[day] === 0;
          const isNonToggleable = isLocked || isDisabled;
          return (
            <div
              key={day}
              title={isLocked
                ? `${fullLabel} — locked (matches start/end date)`
                : isDisabled
                  ? `${fullLabel} — not in date range`
                  : fullLabel}
              onClick={() => !isNonToggleable && handleToggle(day)}
              className={`col-span-1 flex flex-col items-center gap-1 px-2 py-2 rounded-lg transition-all border-2
                ${isDisabled
                  ? 'opacity-40 cursor-not-allowed bg-gray-100 border-gray-200'
                  : isLocked
                    ? 'cursor-default bg-primary-50 border-primary-500'
                    : hasError
                      ? 'bg-white border-danger-500 cursor-pointer'
                      : isSelected
                        ? 'bg-primary-50 border-primary-500 cursor-pointer'
                        : 'bg-white border-gray-300 cursor-pointer'
                }`}
            >
              <div className="relative">
                {isLocked ? (
                  <LockIcon sx={{ fontSize: 16 }} className="text-primary-600" />
                ) : (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => !isNonToggleable && handleToggle(day)}
                    disabled={isNonToggleable}
                    className={`w-4 h-4 accent-primary-500 ${isNonToggleable ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                )}
              </div>
              <span className={`text-sm font-semibold pointer-events-none ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                {label}
              </span>
              <span className="text-xs font-mono text-gray-500 pointer-events-none">
                {day}
              </span>
            </div>
          );
        })}

        {/* Preview */}
        <div className={`col-span-1 flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg border-2 bg-gray-50
          ${hasError ? 'border-danger-500' : 'border-gray-400'}`}>
          <span className="text-lg font-mono font-bold tracking-wider text-gray-800">
            {getPreview()}
          </span>
          {displayFrequency && (
            <span className="text-xs font-semibold text-gray-600">
              {getFrequencyLabel(displayFrequency, freqRate)}
            </span>
          )}
        </div>
      </div>

      {/* Human-readable description */}
      <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg h-9 flex items-center">
        <span className="text-sm leading-5">
          {displayFrequency ? (
            <span className="text-blue-800">
              Selected: <strong>{getFrequencyDescription(displayFrequency, true)}</strong>
              {isMYT && <span className="text-blue-500 font-normal ml-1">(MYT)</span>}
            </span>
          ) : (
            <span className="text-blue-800">No day selected</span>
          )}
        </span>
      </div>

    </div>
  );
}
